import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  List,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  useAuth,
  useCoachingPayment,
  useCoachingPayments,
  useCoachingWorkspace,
} from "@bw/shared/hooks";
import { Page } from "../../../common/components/Page";
import { LessonRow } from "../components/LessonRow";
import { CoachingPaymentRow } from "../components/CoachingPaymentRow";
import { CoachingLessonPanel } from "../components/CoachingLessonPanel";
import { coachingUuid } from "../utils/coachingSelection";

export function MyLessonsPage() {
  const { user, loading } = useAuth();
  if (loading || !user)
    return (
      <Page title="My lessons">
        <Typography role="status">Sign in to view your lessons.</Typography>
      </Page>
    );
  return <MyLessonsBody key={user.uid} />;
}

function MyLessonsBody() {
  const [params] = useSearchParams();
  const location = useLocation();
  const tabs = params.getAll("tab");
  const tab =
    tabs.length === 0
      ? "lessons"
      : tabs.length === 1 && ["lessons", "payments"].includes(tabs[0] ?? "")
        ? tabs[0]
        : null;
  const ids = params.getAll("acceptance_id");
  const selected = ids.length > 0;
  const acceptanceId = ids.length === 1 ? coachingUuid(ids[0]) : null;
  const rawPage = Number(params.get(`${tab}_page`));
  const page =
    !selected && Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  function destination(view: string, nextPage?: number) {
    const next = new URLSearchParams(params);
    next.set("tab", view);
    next.delete("acceptance_id");
    next.delete("revision_id");
    if (nextPage === undefined) next.delete(`${view}_page`);
    else next.set(`${view}_page`, String(nextPage));
    return `${location.pathname}?${next}${location.hash}`;
  }
  return (
    <Page title="My lessons">
      <Tabs value={tab ?? false} aria-label="Coaching views" sx={{ mb: 2 }}>
        <Tab
          component={RouterLink}
          to={destination("lessons", Number(params.get("lessons_page")) || 1)}
          value="lessons"
          label="Lessons"
        />
        <Tab
          component={RouterLink}
          to={destination("payments", Number(params.get("payments_page")) || 1)}
          value="payments"
          label="Payments"
        />
      </Tabs>
      {selected && tab ? (
        <Button
          component={RouterLink}
          to={destination(tab)}
          size="small"
          sx={{ alignSelf: "flex-start" }}
        >
          All {tab}
        </Button>
      ) : null}
      {!tab || params.has("revision_id") || (selected && !acceptanceId) ? (
        <Alert severity="info">This coaching record is unavailable.</Alert>
      ) : selected && acceptanceId ? (
        tab === "payments" ? (
          <SelectedPayment
            key={`payment:${acceptanceId}`}
            acceptanceId={acceptanceId}
          />
        ) : (
          <CoachingLessonPanel
            key={`lesson:${acceptanceId}`}
            acceptanceId={acceptanceId}
          />
        )
      ) : tab === "payments" ? (
        <PaymentList
          key={`payments:${page}`}
          page={page}
          destination={(next) => destination("payments", next)}
        />
      ) : (
        <LessonList
          key={`lessons:${page}`}
          page={page}
          destination={(next) => destination("lessons", next)}
        />
      )}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ flexWrap: "wrap", alignItems: "flex-start" }}
      >
        {tab === "payments" ? (
          <Button component={RouterLink} to="/me/transactions" size="small">
            View all transactions
          </Button>
        ) : null}
        <Button
          component={RouterLink}
          to="/me/coaching-experience"
          size="small"
        >
          View coaching experience
        </Button>
      </Stack>
    </Page>
  );
}

function ReadStatus({
  current,
  error,
  paused,
  label,
}: {
  current: boolean;
  error: boolean;
  paused: boolean;
  label: string;
}) {
  if (paused)
    return (
      <Alert severity="info" role="status">
        Waiting for a connection to refresh your {label}.
      </Alert>
    );
  if (error)
    return (
      <Alert severity="error">
        Couldn&apos;t load your {label}. The record may be unavailable. Please
        refresh to check your access.
      </Alert>
    );
  return current ? null : (
    <Typography role="status">Loading your {label}…</Typography>
  );
}

function SelectedPayment({ acceptanceId }: { acceptanceId: string }) {
  const query = useCoachingPayment(acceptanceId);
  const payment = query.retainedData;
  const matches = payment?.acceptance_id?.toLowerCase() === acceptanceId;
  const current = query.isCurrent && matches;
  return (
    <>
      <ReadStatus
        current={current}
        error={query.isError || (query.isCurrent && !matches)}
        paused={query.fetchStatus === "paused"}
        label="payment"
      />
      {payment && matches ? (
        <List disablePadding hidden={!current} inert={!current}>
          <CoachingPaymentRow payment={payment} canAct={query.canAct} />
        </List>
      ) : null}
    </>
  );
}

function LessonList({
  page,
  destination,
}: {
  page: number;
  destination: (page: number) => string;
}) {
  const query = useCoachingWorkspace(true, (page - 1) * 100);
  const data = query.retainedData;
  return (
    <>
      <ReadStatus
        current={query.isCurrent}
        error={query.isError}
        paused={query.fetchStatus === "paused"}
        label="lessons"
      />
      {data ? (
        <Box hidden={!query.isCurrent} inert={!query.isCurrent}>
          {!data.items.length ? (
            <Typography color="text.secondary" role="status">
              No coaching requests or lessons are available.
            </Typography>
          ) : (
            <List
              disablePadding
              sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
            >
              {data.items.map((item) => (
                <LessonRow key={item.request_id} item={item} />
              ))}
            </List>
          )}
          <Pagination
            page={page}
            total={data.total}
            count={data.items.length}
            destination={destination}
          />
        </Box>
      ) : null}
    </>
  );
}

function PaymentList({
  page,
  destination,
}: {
  page: number;
  destination: (page: number) => string;
}) {
  const query = useCoachingPayments(true, (page - 1) * 100);
  const data = query.retainedData;
  return (
    <>
      <ReadStatus
        current={query.isCurrent}
        error={query.isError}
        paused={query.fetchStatus === "paused"}
        label="payments"
      />
      {data ? (
        <Box hidden={!query.isCurrent} inert={!query.isCurrent}>
          {!data.items.length ? (
            <Typography color="text.secondary" role="status">
              No coaching payments are available.
            </Typography>
          ) : (
            <List
              disablePadding
              sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
            >
              {data.items.map((payment) => (
                <CoachingPaymentRow
                  key={payment.acceptance_id}
                  payment={payment}
                  canAct={query.canAct}
                />
              ))}
            </List>
          )}
          <Pagination
            page={page}
            total={data.total}
            count={data.items.length}
            destination={destination}
          />
        </Box>
      ) : null}
    </>
  );
}

function Pagination({
  page,
  total,
  count,
  destination,
}: {
  page: number;
  total: number;
  count: number;
  destination: (page: number) => string;
}) {
  const navigate = useNavigate();
  if (total <= 100 && page === 1) return null;
  return (
    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
      <Button
        onClick={() => navigate(destination(page - 1))}
        size="small"
        disabled={page === 1}
      >
        Previous
      </Button>
      <Button
        onClick={() => navigate(destination(page + 1))}
        size="small"
        disabled={(page - 1) * 100 + count >= total}
      >
        Next
      </Button>
    </Stack>
  );
}
